import { useState, useEffect } from "react";
import { api } from "@/App";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Car, MapPin, Clock, DollarSign, Star } from "lucide-react";
import { toast } from "sonner";

const History = () => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRides();
  }, []);

  const fetchRides = async () => {
    try {
      const response = await api.get("/customer/rides");
      setRides(response.data);
    } catch (error) {
      toast.error("Failed to load ride history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getVehicleIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'luxury': return '✦';
      case 'premium': return '◆';
      case 'suv': return '■';
      default: return '●';
    }
  };

  if (loading) {
    return (
      <CustomerLayout title="Ride History">
        <div className="space-y-4">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="h-32 rounded-xl animate-shimmer" />
          ))}
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout title="Ride History">
      <div className="space-y-4 animate-fade-in">
        {rides.length > 0 ? (
          rides.map((ride) => (
            <Card 
              key={ride.id} 
              className="bg-[#131316] border-[#2A2A2F] overflow-hidden"
              data-testid={`ride-card-${ride.id}`}
            >
              <CardContent className="p-0">
                <div className="p-4 md:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#1A1A1E] flex items-center justify-center">
                        <span className="text-[#D4AF37] text-xl">{getVehicleIcon(ride.vehicle_type)}</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold capitalize">{ride.vehicle_type} Ride</h3>
                        <p className="text-[#6B6B6B] text-sm">{formatDate(ride.ride_date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[#4ADE80] font-bold text-lg">+{ride.points_earned}</p>
                      <p className="text-[#6B6B6B] text-xs">miles earned</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 p-4 bg-[#0A0A0B] rounded-xl">
                    <div className="text-center">
                      <MapPin size={18} className="mx-auto mb-1 text-[#A0A0A0]" />
                      <p className="text-white font-medium">{ride.distance_miles}</p>
                      <p className="text-[#6B6B6B] text-xs">miles</p>
                    </div>
                    <div className="text-center border-x border-[#2A2A2F]">
                      <DollarSign size={18} className="mx-auto mb-1 text-[#A0A0A0]" />
                      <p className="text-white font-medium">${(ride.fare_cents / 100).toFixed(2)}</p>
                      <p className="text-[#6B6B6B] text-xs">fare</p>
                    </div>
                    <div className="text-center">
                      <Clock size={18} className="mx-auto mb-1 text-[#A0A0A0]" />
                      <p className="text-white font-medium">{formatTime(ride.ride_date)}</p>
                      <p className="text-[#6B6B6B] text-xs">time</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-16">
            <Car size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Rides Yet</h3>
            <p className="text-[#A0A0A0]">Your ride history will appear here</p>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default History;
